import { useEffect } from "react";

import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getOauthUrl } from "@app/analytics/hooks/sso/ssoUtils";
import * as Events from "@app/analytics/tracking/events";
import * as api from "@app/common/auth/api";
import useAuth from "@app/common/auth/useAuth";
import FullPageLoadingSpinnerWithText from "@app/common/components/FullPageLoadingSpinnerWithText";
import useTimeout from "@app/common/hooks/useTimeout";
import ROUTES from "@app/common/routes";
import { withQueryParams } from "@app/common/utils";
import { usePageTitle } from "@app/utils/hooks";
import logger from "@app/utils/logger";

/**
 * This component will attempt to log in users with an encrypted user id
 * If the param is not present or auth fails, the user is redirected to the sign
 * in page.
 */
const SecureSignin = () => {
  usePageTitle("Secure Sign In");
  const { clearUser, fetchAndSetUser } = useAuth();

  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const euid = queryParams.get("euid");
  const action = queryParams.get("action");
  const isOnboarding = queryParams.get("onboarding") === "true";

  const secureSignIn = useMutation({ mutationFn: api.secureSignInUser });

  useTimeout(() => {
    Events.longWait({ page: "secure sign in", duration: 30 });
  }, 30 * 1000);

  const navigateToSignin = (searchParams?: URLSearchParams) => {
    const defaultParams = {
      redirectTo: queryParams.get("redirectTo"),
    };
    navigate(withQueryParams(ROUTES.SIGN_IN, defaultParams, searchParams));
  };

  const navigateToDestination = () => {
    logger.debug("navigating to destination post secure sign in");
    const redirectTo = queryParams.get("redirectTo") ?? ROUTES.LANDING_PAGE;
    Events.logMsg({
      msg: `navigating to destination post secure sign in: ${redirectTo}`,
    });

    if (redirectTo.startsWith("http")) {
      logger.debug("SecureSignin: redirecting via href to", redirectTo);
      window.location.href = redirectTo;
    } else {
      navigate(redirectTo);
    }
  };

  useEffect(() => {
    if (euid) {
      // store the euid so we can use it later for things like
      // deep linking
      Cookies.set("user_cookie", euid);
    }

    if (!euid) {
      logger.info("needed params not found, redirecting to sign in");
      Sentry.captureMessage(
        "navigating to sign in post secure sign in attempt"
      );
      navigateToSignin();
    } else if (action && action === "signin_connect_zoom") {
      logger.info("sign in required before connecting zoom");
      const searchParams = new URLSearchParams(queryParams);
      const authUrl = `${getOauthUrl("zoom", { returnPath: ROUTES.GO.ZOOM_HOME })}&action=add_sso`;

      fetchAndSetUser()
        .then(() => (window.location.href = authUrl))
        .catch(() => {
          searchParams.set("skipOnboarding", "true");
          searchParams.set("externalRedirect", "true");
          searchParams.set("redirectTo", authUrl);
          navigateToSignin(searchParams);
        });
    } else if (!secureSignIn.isPending && !secureSignIn.isSuccess) {
      logger.info("attempting sign in");
      secureSignIn.mutate(
        { euid, provider: "zoom" },
        {
          onSuccess: () => {
            logger.info("successfully signed in via secure sign in");
            clearUser();
            fetchAndSetUser()
              .then(navigateToDestination)
              .catch((error: unknown) => {
                Sentry.captureException(error);
                navigateToSignin();
              });
          },
          onError: (error: any) => {
            Sentry.captureMessage(
              `unable to securely sign in: ${error.message}`
            );
            logger.info(
              "unable to securely sign in, redirecting to sign in page"
            );
            navigateToSignin();
          },
        }
      );
    }
  }, [euid]);

  return (
    <FullPageLoadingSpinnerWithText
      text={isOnboarding ? "Loading Read..." : "Signing you in..."}
    />
  );
};

export default SecureSignin;
